import { HotkeyOptions, useHotkey } from "@tanstack/react-hotkeys";
import { actions } from "@/actions";
import { appRoutes } from "@/app/router";
import { EventContext } from "@/domains/metrics/context";
import { useAppStore } from "@/store";
import { resolveHotkey } from "@/modules/keyboard-shortcuts/platform";
import { KEYBOARD_SHORTCUT_BINDINGS } from "@/modules/keyboard-shortcuts/registry";
import { executeEscapeShortcut } from "@/modules/keyboard-shortcuts/escape";
import { resolveMemItSelectionAttachListState } from "@/modules/keyboard-shortcuts/resolve-mem-it-selection-attach-list-state";
import {
  focusPaneForKeyboardInteraction,
  getFocusedMemItEditableElement,
  getMainPanelSplitViewContainerWidth,
  getVisiblePaneTabs,
  isCleanableNote,
  isNotesListPath,
} from "@/modules/keyboard-shortcuts/helpers";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";
import { NotesListPageStore } from "@/store/pages/NotesListPageStore";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { useCallback } from "react";

const useRelativePinnedItemHotkey = ({
  action,
  index,
  openRelativePinnedItem,
  options,
}: {
  action: keyof typeof KEYBOARD_SHORTCUT_BINDINGS;
  index: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  openRelativePinnedItem: (relativeIndex: number) => void;
  options: HotkeyOptions;
}) => {
  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS[action]),
    () => {
      openRelativePinnedItem(index);
    },
    options
  );
};

export const useAppKeyboardShortcuts = () => {
  const { pageStore, store } = useAppStore();
  const quickSearchIsClosed = !pageStore.quickSearchModal.isOpen;
  const globalHotkeysActive = quickSearchIsClosed && !store.interface.globalHotkeysBlocked;
  const gatedOptions: HotkeyOptions = {
    enabled: globalHotkeysActive,
  };

  // we're too high up in the Provider tree to use useTabPageStore
  const activeTabId = store.navigation.tabs.activeTabId;
  const activeTab = store.navigation.tabs.tabsMap.get(activeTabId) ?? null;
  const mainPanelSplitViewContainerWidth = getMainPanelSplitViewContainerWidth();
  const visiblePaneTabs = getVisiblePaneTabs({
    activeTabId,
    panesMap: store.navigation.tabs.panesMap,
    tabsMap: store.navigation.tabs.tabsMap,
    getResponsivePanes: containerWidth => store.navigation.tabs.getResponsivePanes(containerWidth),
    containerWidth: mainPanelSplitViewContainerWidth,
  });
  const visibleSelectionAttachListStates = visiblePaneTabs
    .map(tab =>
      resolveMemItSelectionAttachListState({
        pageStore,
        activeTab: tab,
        activeTabPath: tab.path,
      })
    )
    .filter((listState): listState is ListStateObservable => !!listState);
  const selectedSelectionAttachListStates = visibleSelectionAttachListStates.filter(
    listState => listState.canChatWithSelection
  );
  const visibleNotesListPageStores = visiblePaneTabs
    .filter(tab => isNotesListPath(tab.path))
    .map(tab => pageStore.getPageStore(tab, tab.path, "notesListPage") as NotesListPageStore | null)
    .filter((notesListPageStore): notesListPageStore is NotesListPageStore => !!notesListPageStore);
  const selectedVisibleNotesListPageStores = visibleNotesListPageStores.filter(
    notesListPageStore => notesListPageStore.listState.isSelectModeActive
  );
  const selectedVisibleNoteIds = Array.from(
    new Set(
      selectedVisibleNotesListPageStores.flatMap(
        notesListPageStore => notesListPageStore.listState.selectedItemIds
      )
    )
  );
  const selectedVisibleNotes = selectedVisibleNoteIds
    .map(noteId => store.notes.getNoteObservableById({ noteId }))
    .filter(note => note != null);
  const tabNotePageStore = activeTab
    ? ((pageStore.getPageStore(
        activeTab,
        activeTab.path,
        "notesViewPage"
      ) as NotesViewPageStore | null) ?? undefined)
    : undefined;
  const tabNotesListPageStore = activeTab
    ? ((pageStore.getPageStore(
        activeTab,
        activeTab.path,
        "notesListPage"
      ) as NotesListPageStore | null) ?? undefined)
    : undefined;
  const hasNoteViewTabOpen = !!tabNotePageStore;
  const hasCrossPaneSelectedNotes = selectedVisibleNoteIds.length > 0;
  const highlightedNotesListItemId = tabNotesListPageStore?.listState.highlightedItemId;
  const notesListMultiSelectIsActive = tabNotesListPageStore?.listState.isSelectModeActive ?? false;
  const canPinHighlightedNotesListItem =
    !notesListMultiSelectIsActive &&
    !!highlightedNotesListItemId &&
    !!tabNotesListPageStore?.orderedItemIds.includes(highlightedNotesListItemId);
  const mayHaveCollectionViewTabOpen =
    !!activeTab?.path &&
    (activeTab.path.startsWith("/collections/") || activeTab.path.startsWith("/c/"));
  const findInNoteIsOpen = tabNotePageStore?.findInNote?.isOpen ?? false;
  const optionsEnabledOnNoteViewTab: HotkeyOptions = {
    enabled: globalHotkeysActive && hasNoteViewTabOpen,
  };
  const optionsEnabledForSelectedNotesOrNoteView: HotkeyOptions = {
    enabled: globalHotkeysActive && (hasCrossPaneSelectedNotes || hasNoteViewTabOpen),
  };
  const optionsEnabledForSelectedNotesOrNoteViewWithOverride: HotkeyOptions = {
    enabled: globalHotkeysActive && (hasCrossPaneSelectedNotes || hasNoteViewTabOpen),
    ignoreInputs: false,
    preventDefault: true,
  };
  const optionsEnabledWithFindInNoteOpen: HotkeyOptions = {
    enabled: globalHotkeysActive && findInNoteIsOpen,
  };
  const optionsEnabledOnNoteViewTabWithPreventDefault: HotkeyOptions = {
    enabled: globalHotkeysActive && hasNoteViewTabOpen,
    preventDefault: true,
  };
  const gatedOptionsWithOverride: HotkeyOptions = {
    enabled: globalHotkeysActive,
    ignoreInputs: false,
    preventDefault: true,
  };
  const canToggleSidebar = !store.layout.shouldHideSidebarToggle;
  const getActiveCollectionsViewPageStore = useCallback(() => {
    if (!activeTab) {
      return undefined;
    }

    return (
      (pageStore.getPageStore(activeTab, activeTab.path, "collectionsViewPage") as {
        handleCreateNewNote: (args?: { autoStartVoice?: boolean }) => Promise<void>;
        collectionObservable?: {
          toggleFavorite: () => Promise<void>;
        } | null;
      } | null) ?? undefined
    );
  }, [activeTab, pageStore]);
  const optionsEnabledForPinNote: HotkeyOptions = {
    enabled:
      globalHotkeysActive &&
      (hasNoteViewTabOpen || mayHaveCollectionViewTabOpen || canPinHighlightedNotesListItem),
  };

  const toggleFocusMode = useCallback(() => {
    if (!canToggleSidebar) return;

    const shouldCloseBoth = store.interface.isSidebarOpen || store.sidePanel.isSidePanelOpen;
    if (shouldCloseBoth) {
      store.interface.setIsSidebarOpen(false);
      store.sidePanel.close();
      return;
    }

    store.interface.setIsSidebarOpen(true);
    store.sidePanel.open();
  }, [canToggleSidebar, store.interface, store.sidePanel]);

  const moveActiveNoteToTrashAndCloseTab = useCallback(async () => {
    if (!tabNotePageStore?.itemObservable) {
      return;
    }

    await tabNotePageStore.itemObservable.moveToTrash();
    store.navigation.tabs.closeTab({ tabId: store.navigation.tabs.activeTabId });
  }, [store.navigation.tabs, tabNotePageStore]);

  const openOrganizeForSelectedNotes = useCallback(() => {
    const primarySelectedNotesListPageStore = selectedVisibleNotesListPageStores[0];
    if (!primarySelectedNotesListPageStore || selectedVisibleNoteIds.length === 0) {
      return false;
    }

    primarySelectedNotesListPageStore.addToCollectionModal.open({
      noteIds: selectedVisibleNoteIds,
      eventContext: primarySelectedNotesListPageStore.eventContext,
    });
    return true;
  }, [selectedVisibleNoteIds, selectedVisibleNotesListPageStores]);

  const autoOrganizeSelectedNotes = useCallback(async () => {
    if (selectedVisibleNoteIds.length === 0) {
      return false;
    }

    selectedVisibleNotesListPageStores.forEach(notesListPageStore =>
      notesListPageStore.listState.clearSelection()
    );
    await actions.autoOrganizeNotes({
      noteIds: selectedVisibleNoteIds,
      store,
    });
    return true;
  }, [selectedVisibleNoteIds, selectedVisibleNotesListPageStores, store]);

  const chatWithCombinedSelection = useCallback(async () => {
    if (selectedSelectionAttachListStates.length === 0) {
      return false;
    }

    const noteIds = Array.from(
      new Set(selectedSelectionAttachListStates.flatMap(listState => listState.selectedChatNoteIds))
    );
    const collectionIds = Array.from(
      new Set(
        selectedSelectionAttachListStates.flatMap(listState => listState.selectedChatCollectionIds)
      )
    );

    if (noteIds.length === 0 && collectionIds.length === 0) {
      return false;
    }

    store.sidePanel.requestMemItInputFocus();
    selectedSelectionAttachListStates.forEach(listState => listState.clearSelection());
    await actions.startChatWithEntities({
      noteIds,
      collectionIds,
      store,
      overrideContext: false,
    });
    return true;
  }, [selectedSelectionAttachListStates, store]);

  const trashSelectedNotes = useCallback(async () => {
    if (!hasCrossPaneSelectedNotes || selectedVisibleNotes.length === 0) {
      return false;
    }

    selectedVisibleNotesListPageStores.forEach(notesListPageStore =>
      notesListPageStore.listState.clearSelection()
    );
    actions.moveNotesToTrash({
      notes: selectedVisibleNotes,
      store,
    });
    return true;
  }, [hasCrossPaneSelectedNotes, selectedVisibleNotes, selectedVisibleNotesListPageStores, store]);

  const goToRelativeTab = useCallback(
    (relativeIndex: number) => {
      const paneTabs = store.navigation.tabs.getTabsForPane(store.navigation.tabs.activePaneId);
      const targetTab = paneTabs[relativeIndex - 1];
      if (!targetTab) return;

      store.navigation.tabs.switchToTab({ tabId: targetTab.id });
    },
    [store.navigation.tabs]
  );

  const openRelativePinnedItem = useCallback(
    (relativeIndex: number) => {
      const pinnedItem = store.favoriteItems.sortedFavoriteItems[relativeIndex - 1];
      if (!pinnedItem) return;

      switch (pinnedItem.itemKind) {
        case "COLLECTION":
          store.navigation.goToCollection({ collectionId: pinnedItem.itemId });
          return;
        case "NOTE":
          store.navigation.goToNote({ noteId: pinnedItem.itemId });
          return;
        default:
          return;
      }
    },
    [store.favoriteItems.sortedFavoriteItems, store.navigation]
  );

  const switchToNextPaneAndFocus = useCallback(() => {
    const orderedPanes =
      store.navigation.tabs.panes ??
      Array.from(store.navigation.tabs.panesMap.values()).sort((a, b) => a.order - b.order);
    if (orderedPanes.length <= 1) {
      return;
    }

    const currentPaneIndex = orderedPanes.findIndex(
      pane => pane.id === store.navigation.tabs.activePaneId
    );
    const normalizedCurrentPaneIndex = currentPaneIndex >= 0 ? currentPaneIndex : 0;
    const nextPane = orderedPanes[(normalizedCurrentPaneIndex + 1) % orderedPanes.length];
    if (!nextPane) {
      return;
    }

    store.navigation.tabs.switchToNextPane();
    focusPaneForKeyboardInteraction(nextPane.id);
  }, [store.navigation.tabs]);

  const activeNoteId = tabNotePageStore?.noteId;

  // Global menu action Keyboard shortcuts
  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.create_note),
    () => {
      if (store.notes.newNoteIsBeingCreated) return;
      const activeCollectionsViewPageStore = getActiveCollectionsViewPageStore();
      if (activeCollectionsViewPageStore) {
        void activeCollectionsViewPageStore.handleCreateNewNote();
        return;
      }
      store.notes.composeNewNote({ eventContext: EventContext.KeyboardShortcut });
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.create_note_voice),
    () => {
      if (store.notes.newNoteIsBeingCreated) return;
      const activeCollectionsViewPageStore = getActiveCollectionsViewPageStore();
      if (activeCollectionsViewPageStore) {
        void activeCollectionsViewPageStore.handleCreateNewNote({ autoStartVoice: true });
        return;
      }
      store.notes.composeNewNote({
        eventContext: EventContext.KeyboardShortcut,
        autoStartVoice: true,
      });
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.search),
    () => {
      pageStore.quickSearchModal.toggleIsOpen();
    },
    {
      enabled: !store.interface.globalHotkeysBlocked,
    }
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.open_mem_it),
    async () => {
      if (await chatWithCombinedSelection()) {
        return;
      }

      store.sidePanel.goToMemIt(undefined, { openChatPanel: true, focusInput: true });
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.show_heads_up),
    () => {
      store.sidePanel.goToHeadsUp();
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.see_keyboard_shortcuts),
    store.interface.openKeyboardShortcutsModal,
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_back),
    () => {
      store.navigation.goBack();
    },
    gatedOptionsWithOverride
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_forward),
    () => {
      store.navigation.goForward();
    },
    gatedOptionsWithOverride
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_home),
    () => {
      store.navigation.goToNotes();
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_collections),
    () => {
      store.navigation.goToCollections();
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_settings),
    () => {
      store.settingsModal.open();
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.new_tab),
    () => {
      store.navigation.tabs.openInNewTab({ path: store.navigation.tabs.NEW_TAB_PATH });
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.close_tab),
    () => {
      const activeTab = store.navigation.tabs.tabsMap.get(store.navigation.tabs.activeTabId);
      if (!activeTab) {
        return;
      }

      if (!activeTab.isDefault) {
        store.navigation.tabs.closeTab({ tabId: store.navigation.tabs.activeTabId });
        return;
      }

      const activePaneTabs = store.navigation.tabs.getTabsForPane(activeTab.paneId);
      const isBareHomePane = activePaneTabs.length === 1;
      const hasMultipleVisiblePanes = visiblePaneTabs.length >= 2;

      if (isBareHomePane && hasMultipleVisiblePanes) {
        store.navigation.tabs.closePane({ paneId: activeTab.paneId });
      }
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_1),
    () => {
      goToRelativeTab(1);
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_2),
    () => {
      goToRelativeTab(2);
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_3),
    () => {
      goToRelativeTab(3);
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_4),
    () => {
      goToRelativeTab(4);
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_5),
    () => {
      goToRelativeTab(5);
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_6),
    () => {
      goToRelativeTab(6);
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_7),
    () => {
      goToRelativeTab(7);
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_8),
    () => {
      goToRelativeTab(8);
    },
    gatedOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_9),
    () => {
      goToRelativeTab(9);
    },
    gatedOptions
  );

  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_1",
    index: 1,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_2",
    index: 2,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_3",
    index: 3,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_4",
    index: 4,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_5",
    index: 5,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_6",
    index: 6,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_7",
    index: 7,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_8",
    index: 8,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_9",
    index: 9,
    openRelativePinnedItem,
    options: gatedOptions,
  });

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.move_notes_to_trash),
    async () => {
      if (await trashSelectedNotes()) {
        return;
      }

      await moveActiveNoteToTrashAndCloseTab();
    },
    optionsEnabledForSelectedNotesOrNoteViewWithOverride
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.escape),
    () => {
      const focusedMemItEditableElement = getFocusedMemItEditableElement();
      if (focusedMemItEditableElement) {
        focusedMemItEditableElement.blur();
        return;
      }

      const listState = resolveMemItSelectionAttachListState({
        pageStore,
        activeTab,
        activeTabPath: activeTab?.path ?? store.navigation.tabs.NEW_TAB_PATH,
      });

      if (listState?.isSelectModeActive) {
        listState.reset();
        return;
      }

      if (listState?.keyboardHighlightedItemId) {
        listState.highlightItem();
        return;
      }

      if (tabNotePageStore) {
        executeEscapeShortcut({ store, notesViewPageStore: tabNotePageStore });
      } else {
        store.navigation.tabs.openInNewTab({ path: appRoutes.notesList({}).path });
      }
    },
    gatedOptions
  );

  useHotkey("Mod+.", switchToNextPaneAndFocus, {
    ...gatedOptions,
    preventDefault: true,
  });

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.toggle_mem_panel),
    () => {
      store.sidePanel.toggle();
    },
    gatedOptionsWithOverride
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.toggle_sidebar),
    () => {
      if (!canToggleSidebar) return;
      store.interface.toggleSidebar();
    },
    gatedOptionsWithOverride
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.focus_mode),
    toggleFocusMode,
    gatedOptionsWithOverride
  );

  // Editor Keyboard shortcuts
  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.editor_find),
    () => tabNotePageStore?.findInNote?.open(),
    optionsEnabledOnNoteViewTab
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.editor_find_next),
    () => tabNotePageStore?.findInNote?.findNext(),
    optionsEnabledWithFindInNoteOpen
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.organize_selected_notes),
    () => {
      if (openOrganizeForSelectedNotes()) {
        return;
      }

      if (!activeNoteId) return;

      tabNotePageStore?.requestOpenOrganizeMenu();
    },
    optionsEnabledForSelectedNotesOrNoteView
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.auto_organize_selected_notes),
    async () => {
      if (await autoOrganizeSelectedNotes()) {
        return;
      }

      if (!activeNoteId) return;

      await actions.autoOrganizeNotes({
        noteIds: [activeNoteId],
        store,
      });
    },
    optionsEnabledForSelectedNotesOrNoteViewWithOverride
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.find_in_note_previous_result_alternate),
    () => {
      tabNotePageStore?.findInNote?.findPrevious();
    },
    optionsEnabledWithFindInNoteOpen
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.open_note_in_split_view),
    () => {
      if (!activeNoteId) return;

      store.navigation.goToNote({
        noteId: activeNoteId,
        activeTabId: activeTab?.id,
        openInInactivePane: true,
      });
    },
    optionsEnabledOnNoteViewTab
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.move_note_to_other_split_view),
    () => {
      if (!activeNoteId || !activeTab) return;

      store.navigation.goToNote({
        noteId: activeNoteId,
        activeTabId: activeTab.id,
        openInInactivePane: true,
      });
      store.navigation.tabs.closeTab({ tabId: activeTab.id });
    },
    optionsEnabledOnNoteViewTab
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.cleanup_note),
    async () => {
      const note = tabNotePageStore?.itemObservable;
      if (!isCleanableNote(note)) return;

      await note.cleanup();
    },
    optionsEnabledOnNoteViewTab
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.cleanup_and_auto_organize_note),
    async () => {
      const note = tabNotePageStore?.itemObservable;
      if (!activeNoteId || !isCleanableNote(note)) return;

      const cleanupResult = await note.cleanup();
      if (!cleanupResult.success) return;

      await actions.autoOrganizeNotes({
        noteIds: [activeNoteId],
        store,
      });
    },
    optionsEnabledOnNoteViewTab
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.copy_note_link),
    async () => {
      if (!activeNoteId) return;

      await actions.copyNoteLinkToClipboard({ noteId: activeNoteId });
    },
    optionsEnabledOnNoteViewTabWithPreventDefault
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.pin_note),
    () => {
      if (tabNotePageStore?.itemObservable) {
        void tabNotePageStore.itemObservable.toggleFavorite();
        return;
      }

      const activeCollectionsViewPageStore = getActiveCollectionsViewPageStore();
      if (activeCollectionsViewPageStore?.collectionObservable) {
        void activeCollectionsViewPageStore.collectionObservable.toggleFavorite();
        return;
      }

      if (!canPinHighlightedNotesListItem || !highlightedNotesListItemId) {
        return;
      }

      void store.notes
        .getNoteObservableById({ noteId: highlightedNotesListItemId })
        ?.toggleFavorite();
    },
    optionsEnabledForPinNote
  );
};
